import { useContext, useEffect, useMemo, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';

import { BrewerySessionContext, defaultBrewerySession } from '../context/BrewerySessionContext';
import { useBreweryGroupMembers } from './useBeerAvailability';
import { formatBreweryMemberLabel } from '../utils/brewery/formatBreweryMemberLabel';
import { db } from '../utils/firebase';

function trimBreweryId(breweryId) {
  return typeof breweryId === 'string' ? breweryId.trim() : '';
}

function trimGroupId(groupId) {
  return typeof groupId === 'string' ? groupId.trim() : '';
}

function mapSessionRowToLocation(row) {
  return {
    id: row.breweryId,
    name: row.breweryName || row.breweryId,
    city: row.breweryCity || '',
    label: formatBreweryMemberLabel({
      name: row.breweryName,
      city: row.breweryCity,
    }),
  };
}

function mapGroupMemberToLocation(member) {
  return {
    id: member.id,
    name: member.name || member.id,
    city: member.city || '',
    label: formatBreweryMemberLabel(member),
  };
}

/**
 * Resolves selectable brewery locations for the unified Add Beer dialog.
 * Prefers Brewhouse session permissions; falls back to group members for Engage/global admin.
 */
export function useAddBeerLocationOptions({
  primaryBreweryId,
  breweryGroupId,
  primaryBreweryName = '',
  primaryBreweryCity = '',
  enabled = true,
}) {
  const session = useContext(BrewerySessionContext) ?? defaultBrewerySession;
  const normalizedPrimaryId = trimBreweryId(primaryBreweryId);
  const propGroupId = trimGroupId(breweryGroupId);
  const sessionGroupId = trimGroupId(session.breweryGroupId);
  const [fetchedGroupId, setFetchedGroupId] = useState('');
  const [fetchingGroupId, setFetchingGroupId] = useState(false);

  const resolvedGroupId = propGroupId || sessionGroupId || fetchedGroupId;
  const {
    members: groupMembers,
    loading: groupMembersLoading,
  } = useBreweryGroupMembers(enabled ? resolvedGroupId : '');

  useEffect(() => {
    if (!enabled || !normalizedPrimaryId || propGroupId || sessionGroupId) {
      setFetchedGroupId('');
      setFetchingGroupId(false);
      return undefined;
    }

    let cancelled = false;
    setFetchingGroupId(true);

    (async () => {
      try {
        const brewerySnap = await getDoc(doc(db, 'breweries', normalizedPrimaryId));
        if (cancelled) {
          return;
        }
        const rawGroupId =
          brewerySnap.exists() && typeof brewerySnap.data()?.breweryGroupId === 'string'
            ? brewerySnap.data().breweryGroupId.trim()
            : '';
        setFetchedGroupId(rawGroupId);
      } catch (fetchError) {
        console.error('useAddBeerLocationOptions: failed to load brewery group id', fetchError);
        if (!cancelled) {
          setFetchedGroupId('');
        }
      } finally {
        if (!cancelled) {
          setFetchingGroupId(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, normalizedPrimaryId, propGroupId, sessionGroupId]);

  const selectableLocations = useMemo(() => {
    if (!enabled || !normalizedPrimaryId) {
      return [];
    }

    const normalizedGroupId = trimGroupId(resolvedGroupId);
    if (normalizedGroupId) {
      const sessionRows = Array.isArray(session.breweries)
        ? session.breweries.filter(
            (row) => trimGroupId(row.breweryGroupId) === normalizedGroupId,
          )
        : [];

      if (sessionRows.length > 0) {
        return sessionRows.map(mapSessionRowToLocation);
      }

      if (groupMembers.length > 1) {
        return groupMembers.map(mapGroupMemberToLocation);
      }
    }

    return [
      {
        id: normalizedPrimaryId,
        name: primaryBreweryName || normalizedPrimaryId,
        city: primaryBreweryCity || '',
        label: formatBreweryMemberLabel({
          name: primaryBreweryName || normalizedPrimaryId,
          city: primaryBreweryCity,
        }),
      },
    ];
  }, [
    enabled,
    groupMembers,
    normalizedPrimaryId,
    primaryBreweryCity,
    primaryBreweryName,
    resolvedGroupId,
    session.breweries,
  ]);

  const defaultSelectedIds = useMemo(
    () => selectableLocations.map((location) => location.id),
    [selectableLocations],
  );

  return {
    selectableLocations,
    defaultSelectedIds,
    showLocationSelector: selectableLocations.length > 1,
    loading: fetchingGroupId || groupMembersLoading,
    resolvedGroupId,
  };
}
